import { Signal, VALID_BBB_GRADES } from "@/constants/signal/signal"
import { StatusType } from "@/constants/status-type"
import type { MethodologyOutput } from "@/types/signal-types"

export type CharacterBreakdown =
  MethodologyOutput["companyCharacter"]["breakdown"]

// Display order - shared between BackgroundCheckSection and HowWeGotScoreSection
export const BACKGROUND_CHECK_SIGNALS = [
  Signal.TRACK_RECORD,
  Signal.BBB,
  Signal.CLUSTERING,
] as const

type BackgroundCheckConfig = {
  getStatus: (breakdown: CharacterBreakdown) => StatusType
  getHeadline: (breakdown: CharacterBreakdown) => string
  getDescription: (breakdown: CharacterBreakdown) => string | null
}

export const dataByBackgroundCheckSignal: Partial<
  Record<Signal, BackgroundCheckConfig>
> = {
  [Signal.TRACK_RECORD]: {
    getStatus: (breakdown) => {
      const years = breakdown.track_record?.yearsInBusiness
      if (years == null) return StatusType.NO_DATA
      if (years >= 10) return StatusType.GOOD
      if (years <= 5) return StatusType.CONCERN
      return StatusType.BASELINE // Q2 (6-9) = baseline
    },
    getHeadline: (breakdown) => {
      const years = breakdown.track_record?.yearsInBusiness
      if (years == null) return "Years in Business"
      return `Serving customers for ${years} years`
    },
    getDescription: (breakdown) => {
      const years = breakdown.track_record?.yearsInBusiness
      if (years == null) return null
      if (years >= 13)
        return "Among the longest-standing installers in the market."
      if (years >= 10)
        return "Operating longer than most installers in the market."
      if (years <= 5) return "Newer than most installers in the market."
      return null // Q2 (6-9) = no statement
    },
  },
  [Signal.BBB]: {
    getStatus: (breakdown) => {
      const grade = breakdown.bbb.grade
      if (!grade || !VALID_BBB_GRADES.has(grade)) return StatusType.NO_DATA
      if (grade === "A+" || grade === "A" || grade === "A-")
        return StatusType.GOOD
      if (grade.startsWith("B")) return StatusType.CAUTION
      return StatusType.CONCERN // C and below
    },
    getHeadline: (breakdown) => {
      const grade = breakdown.bbb.grade
      return grade && VALID_BBB_GRADES.has(grade)
        ? `BBB Rating: ${grade}`
        : "BBB Rating"
    },
    getDescription: (breakdown) => {
      const grade = breakdown.bbb.grade
      if (!grade || !VALID_BBB_GRADES.has(grade)) return "Not BBB rated."
      if (grade === "A+")
        return "Excellent BBB standing. Strong complaint resolution."
      if (grade === "A" || grade === "A-") return "Good BBB standing."
      if (grade.startsWith("B"))
        return "Mixed BBB standing. Some unresolved complaints."
      return "Poor BBB standing. Significant complaints."
    },
  },
  [Signal.CLUSTERING]: {
    getStatus: (breakdown) => {
      if (!breakdown.clustering) return StatusType.NO_DATA
      return breakdown.clustering.triggered
        ? StatusType.CAUTION
        : StatusType.GOOD
    },
    getHeadline: (breakdown) => {
      if (!breakdown.clustering) return "Review Patterns"
      return breakdown.clustering.triggered
        ? "Unnatural Review Patterns"
        : "Natural Review Patterns"
    },
    getDescription: (breakdown) => {
      if (!breakdown.clustering) return null
      return breakdown.clustering.triggered
        ? "Some periods had unusually high review activity."
        : "Reviews were posted naturally over time."
    },
  },
}
